// tslint:disable: no-inferrable-types
// tslint:disable: max-line-length
// tslint:disable: member-ordering
import { Injectable } from '@angular/core';
import { SYSTEM_ROLE_ID } from '@fry/lib/roles/roles.system';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { FormEditorService } from '../form-editor/form-editor.service';
import {
  WorkflowAuditResult,
  WorkflowAuditService
} from '../workflow-audit';
import { BackendObjectRef } from './backend-integration.service';
import { FormFieldTypeGeneral, FormFieldTypeUserField } from './form-field-type.model';
import { State } from './state.model';
import { TransitionAction } from './transition-action.model';
import { TransitionGuard } from './transition-guard.model';
import { Transition } from './transition.model';
import { Workflow } from './workflow.model';

export interface SelectControlGroup<T> {
  name: string;
  items: T[];
}

@Injectable()
export class WorkflowEditorStore {
  public availableRoles: BackendObjectRef[] = [];
  public availableActions: TransitionAction[] = [];
  public availableGuards: TransitionGuard[] = [];
  public availableGeneralFields: FormFieldTypeGeneral[] = [];
  public availableUserFields: FormFieldTypeUserField[] = [];

  private _isDirty$$ = new BehaviorSubject<boolean>(false);
  public get isDirty$() { return this._isDirty$$.asObservable(); }
  public get isDirty() { return this._isDirty$$.value; }
  public set isDirty(dirty: boolean) {
    if (this.isDirty === dirty) { return; }
    this._isDirty$$.next(dirty);
  }

  private _workflow$$ = new BehaviorSubject<Workflow|undefined>(undefined);
  public get workflow$() { return this._workflow$$.asObservable(); }
  public get workflow(): Workflow|undefined { return this._workflow$$.value; }
  public set workflow(workflow: Workflow|undefined) { this._workflow$$.next(workflow); }

  private _formEditorSubscription?: Subscription;

  private _auditService: WorkflowAuditService;
  public get auditResult$(): Observable<WorkflowAuditResult> {
    return this._auditService.result$;
  }

  public constructor() {
    this._auditService = new WorkflowAuditService();
  }

  public initialize(workflow: Workflow,
                    availableRoles: BackendObjectRef[],
                    availableActions: TransitionAction[],
                    availableGuards: TransitionGuard[],
                    availableGeneralFields: FormFieldTypeGeneral[],
                    availableUserFields: FormFieldTypeUserField[],
                    dirty: boolean): void {
      // Any changes to Workflow should propagate changes downstream
      this.workflow$.subscribe(wfl => {
        if (!wfl) { return; }

        this._isDirty$$.next(true);
        this._transitions$$.next(wfl.transitions);
        this._auditService.auditWorkflow(wfl);
      });

      this.availableRoles = availableRoles;
      this.availableActions = availableActions;
      this.availableGuards = availableGuards;
      this.availableGeneralFields = availableGeneralFields;
      this.availableUserFields = availableUserFields;

      // Set the workflow, this will propagate the changes
      this._workflow$$.next(workflow);
      this._isDirty$$.next(dirty);
  }

  public createFormEditor(): FormEditorService {
    if (this._formEditorSubscription) {
      this._formEditorSubscription.unsubscribe();
      this._formEditorSubscription = undefined;
    }

    if (!this.workflow) {
      throw new Error($localize `Workflow is not initialised yet!`);
    }

    const formEditor = new FormEditorService(this);

    // Any changes to the Form should be reflected in the Workflow
    this._formEditorSubscription =
      formEditor.form$.subscribe(form => {
        if (!this.workflow) { return; }
        if (this.workflow.form === form) { return; }

        const workflow = this.workflow.copy();
        workflow.form = form;
        this._workflow$$.next(workflow);
      });

    return formEditor;
  }

  //
  // Transitions
  //

  private _transitions$$ = new BehaviorSubject<Transition[]>([]);
  public get transitions$() { return this._transitions$$.asObservable(); }
  public get transitions() { return this._transitions$$.value; }

  public addTransition(value: Transition | Partial<Transition>): void {
    if (!this.workflow) { return; } // TODO: better

    let transition: Transition;
    if (value instanceof Transition) {
      transition = value;
    } else {
      transition = Transition.transitionFromPartial(value);
      transition.isDraft = false;
    }

    const workflow = this.workflow.copy();
    if (transition.source.isDraft) { workflow.addState(transition.source); }
    if (transition.target.isDraft) { workflow.addState(transition.target); }
    workflow.addTransition(transition);
    this._workflow$$.next(workflow);
  }

  public deleteTransition(transitionID: string): void {
    if (!this.workflow) { return; } // TODO: better

    const workflow = this.workflow.copy();
    workflow.deleteTransition(transitionID);
    this._workflow$$.next(workflow);
  }

  public updateTransition(transitionID: string, update: Partial<Transition>): void {
    if (!this.workflow) { return; } // TODO: better

    const workflow = this.workflow.copy();
    const original = workflow.transitionByID(transitionID);
    const patch: Partial<Transition> = {...original, ...update};
    if (update.source) {
      update.source.isDraft
      ? workflow.addState(update.source)
      : workflow.updateState(update.source);
    }
    if (update.target && update.target.isDraft) {
      workflow.addState(update.target);
    }
    if (update.target && !update.target.isDraft) {
      workflow.updateState(update.target);
    }
    workflow.updateTransition(Transition.transitionFromPartial(patch));
    this._workflow$$.next(workflow);
  }

  //
  // States
  //

  /*
  private states$$: BehaviorSubject<State[]> = new BehaviorSubject<State[]>([]);
  public get states$(): Observable<State[]> { return this.states$$.asObservable(); }
  public get states(): State[] { return this.states$$.value; }

  public addState(state: State): void {
      state.isDraft = false;
      this.states$$.next([...this.states, state]);
  }
  */

  /*
  public deleteState(stateID: string): void {
    if (!this.workflow) { return; } // TODO: better

    const workflow = this.workflow.copy();
    workflow.deleteState(stateID);
    this._workflow$$.next(workflow);;
  }
  */

  public updateState(update: State): void {
    if (!this.workflow) { return; } // TODO: better
    update.isDraft = false;

    const workflow = this.workflow.copy();
    workflow.updateState(update);
    this._workflow$$.next(workflow);
  }

  // UI management
  private focusedTransition$$ = new BehaviorSubject<Transition|undefined>(undefined);

  public get focusedTransition$(): Observable<Transition|undefined> {
      return this.focusedTransition$$.asObservable();
  }

  public get focusedTransition(): Transition|undefined {
      return this.focusedTransition$$.value;
  }

  public focusTransition(transitionID: string|undefined): void {
    if (!this.workflow) { return; }

    const transition = transitionID
                       ? this.workflow.transitionByID(transitionID)
                       : undefined;
    this.focusedTransition$$.next(transition);
  }

  public createRolesSelectData(): SelectControlGroup<BackendObjectRef>[] {
    return WorkflowEditorStore.createRolesSelectData(this.availableRoles,
                                                     this.workflow?.roles);
  }

  public static createRolesSelectData(availableRoles: BackendObjectRef[],
                                      usedRoles?: BackendObjectRef[]): SelectControlGroup<BackendObjectRef>[] {
    const workflowRoles = usedRoles ?? [];
    const nonWorkflowRoles = availableRoles.filter(ar => !workflowRoles.find(wr => wr.id === ar.id));

    // If workflow roles don't have the Timeline owner yet.
    // Try to find system role Timeline Owner definded in SYSTEM_ROLES
    // '@fry/lib/roles/roles.system'
    // If we have the Timeline owner available, move it to the workflow roles.
    const wrTLOindex = workflowRoles.findIndex(r => r.id === SYSTEM_ROLE_ID.TIMELINE_OWNER);
    if (wrTLOindex > -1) {
      workflowRoles.unshift(workflowRoles.splice(wrTLOindex, 1)[0]);
    } else {
      const idx = nonWorkflowRoles.findIndex(r => r.id === SYSTEM_ROLE_ID.TIMELINE_OWNER);
      if (idx > -1) {
        workflowRoles.unshift(nonWorkflowRoles.splice(idx, 1)[0]);
      }
    }

    const result = [];
    if (workflowRoles.length > 0) {
      result.push({ name: $localize `Suggested roles`, items: workflowRoles});
    }
    if (nonWorkflowRoles.length > 0) {
      result.push({ name: $localize `Available roles`, items: nonWorkflowRoles});
    }
    return result;
  }
}
